import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import '../styles/EditProfile.css';

const DENOMINATIONS = [
  'Catholic', 'Protestant', 'Pentecostal', 'Orthodox', 'Non-denominational',
  'Anglican', 'Baptist', 'Methodist', 'Adventist', 'Evangelical', 'Other',
];

const FAITH_LEVELS = [
  { value: 1, label: 'Exploring' },
  { value: 2, label: 'Believer' },
  { value: 3, label: 'Practicing' },
  { value: 4, label: 'Devoted' },
  { value: 5, label: 'Faith-led' },
];

const INTERESTS = [
  'Worship', 'Bible study', 'Prayer groups', 'Volunteering',
  'Mission trips', 'Travel', 'Music', 'Reading',
  'Hiking', 'Cooking', 'Photography', 'Sports',
  'Art', 'Movies', 'Coffee', 'Family time',
  'Fitness', 'Gaming', 'Writing', 'Dancing',
];

const MAX_INTERESTS = 8;
const MAX_PHOTOS = 6;

export default function EditProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [denomination, setDenomination] = useState('');
  const [faithLevel, setFaithLevel] = useState(1);
  const [homeChurch, setHomeChurch] = useState('');
  const [bio, setBio] = useState('');
  const [interests, setInterests] = useState([]);

  useEffect(() => {
    let cancelled = false;
    API.get('/api/profile/me')
      .then((res) => {
        if (cancelled) return;
        const p = res.data;
        setProfile(p);
        setFullName(p.full_name || '');
        setDateOfBirth(p.date_of_birth ? p.date_of_birth.split('T')[0] : '');
        setGender(p.gender || '');
        setDenomination(p.denomination || '');
        setFaithLevel(p.faith_level || 1);
        setHomeChurch(p.home_church || '');
        setBio(p.bio || '');
        setInterests(p.interests || []);
        setPhotos(p.photos || []);
      })
      .catch((err) => {
        if (!cancelled) setError('Failed to load profile');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const toggleInterest = (interest) => {
    if (interests.includes(interest)) {
      setInterests(interests.filter((i) => i !== interest));
    } else if (interests.length < MAX_INTERESTS) {
      setInterests([...interests, interest]);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      alert(`Maximum ${MAX_PHOTOS} photos`);
      return;
    }

    const formData = new FormData();
    formData.append('photo', file);

    setUploadingPhoto(true);
    try {
      const response = await API.post('/api/photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotos([...photos, response.data]);
    } catch (err) {
      alert(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await API.delete(`/api/photos/${photoId}`);
      setPhotos(photos.filter((p) => p.id !== photoId));
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const handleSave = async () => {
    setError('');
    if (!fullName.trim()) {
      setError('Name is required');
      return;
    }
    if (!denomination) {
      setError('Please select a denomination');
      return;
    }

    setSaving(true);
    try {
      await API.put('/api/profile', {
        full_name: fullName.trim(),
        date_of_birth: dateOfBirth,
        gender,
        denomination,
        faith_level: faithLevel,
        home_church: homeChurch.trim(),
        bio: bio.trim(),
        interests,
      });
      navigate('/settings');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="edit-profile-page"><div className="ep-loading">Loading...</div></div>;
  }

  return (
    <div className="edit-profile-page">
      <header className="ep-header">
        <button className="ep-back" onClick={() => navigate(-1)} aria-label="Back">
          ←
        </button>
        <h1>Edit profile</h1>
        <div className="ep-spacer" />
      </header>

      <div className="ep-content">
        {/* Photos */}
        <section className="ep-section">
          <h2>Photos</h2>
          <p className="ep-hint">Up to {MAX_PHOTOS} photos. Tap an image to delete.</p>
          <div className="photo-grid">
            {photos.map((photo) => (
              <div key={photo.id} className="photo-tile">
                <img src={photo.url} alt="" />
                <button
                  className="photo-delete"
                  onClick={() => handlePhotoDelete(photo.id)}
                  aria-label="Delete photo"
                >
                  ×
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <button
                className="photo-upload"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
              >
                {uploadingPhoto ? '...' : '+'}
              </button>
            )}
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoUpload}
            />
          </div>
        </section>

        {/* Basics */}
        <section className="ep-section">
          <h2>Basics</h2>

          <label className="ep-label">Full name</label>
          <input
            type="text"
            className="ep-input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Your name"
          />

          <label className="ep-label">Date of birth</label>
          <input
            type="date"
            className="ep-input"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
          />

          <label className="ep-label">Gender</label>
          <div className="ep-chip-row">
            {['male', 'female'].map((g) => (
              <button
                key={g}
                className={`ep-chip ${gender === g ? 'selected' : ''}`}
                onClick={() => setGender(g)}
              >
                {g === 'male' ? 'Man' : 'Woman'}
              </button>
            ))}
          </div>
        </section>

        {/* Faith */}
        <section className="ep-section">
          <h2>Faith</h2>

          <label className="ep-label">Denomination</label>
          <div className="ep-chip-row wrap">
            {DENOMINATIONS.map((d) => (
              <button
                key={d}
                className={`ep-chip ${denomination === d ? 'selected' : ''}`}
                onClick={() => setDenomination(d)}
              >
                {d}
              </button>
            ))}
          </div>

          <label className="ep-label">Faith level</label>
          <div className="ep-faith-grid">
            {FAITH_LEVELS.map((lvl) => (
              <button
                key={lvl.value}
                className={`ep-faith-card ${faithLevel === lvl.value ? 'selected' : ''}`}
                onClick={() => setFaithLevel(lvl.value)}
              >
                <span className="faith-num">{lvl.value}</span>
                <span className="faith-label">{lvl.label}</span>
              </button>
            ))}
          </div>

          <label className="ep-label">Home church (optional)</label>
          <input
            type="text"
            className="ep-input"
            value={homeChurch}
            onChange={(e) => setHomeChurch(e.target.value)}
            placeholder="e.g. Grace Community Church"
          />
        </section>

        {/* About */}
        <section className="ep-section">
          <h2>About</h2>

          <label className="ep-label">Bio</label>
          <textarea
            className="ep-input"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell people a bit about yourself..."
            rows={4}
            maxLength={500}
          />
          <p className="ep-counter">{bio.length} / 500</p>

          <label className="ep-label">
            Interests ({interests.length} / {MAX_INTERESTS})
          </label>
          <div className="ep-chip-row wrap">
            {INTERESTS.map((interest) => {
              const selected = interests.includes(interest);
              const disabled = !selected && interests.length >= MAX_INTERESTS;
              return (
                <button
                  key={interest}
                  className={`ep-chip ${selected ? 'selected' : ''}`}
                  onClick={() => toggleInterest(interest)}
                  disabled={disabled}
                >
                  {interest}
                </button>
              );
            })}
          </div>
        </section>

        {error && <div className="ep-error">{error}</div>}
      </div>

      <div className="ep-save-bar">
        <button
          className="ep-save-button"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}